import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { HomeSummary, RecurringExpense, TransactionPage } from '../api/types';
import { formatEuro } from '../lib/format';
import { currentPeriod } from '../lib/period';
import { TransactionTimeline } from '../components/TransactionRow';
import { Card, EmptyState, Gauge, SectionTitle, Spinner, Tile } from '../components/ui';

/**
 * Home: risponde a "quanto posso ancora spendere?" prima di ogni altra cosa.
 * L'anello e' il budget del mese, la riga sotto e' il ritmo di spesa, poi la
 * timeline dei movimenti recenti.
 */
export function HomePage() {
  const period = currentPeriod();

  const { data: summary, isLoading } = useQuery({
    queryKey: ['summary', 'home', period.year, period.month],
    queryFn: () => api.get<HomeSummary>(`/summary/home?year=${period.year}&month=${period.month}`),
  });

  const { data: recent } = useQuery({
    queryKey: ['transactions', 'recent'],
    queryFn: () => api.get<TransactionPage>('/transactions?limit=25'),
  });

  const { data: due } = useQuery({
    queryKey: ['recurring', 'due', period.year, period.month],
    queryFn: () =>
      api.get<RecurringExpense[]>(`/recurring/due?year=${period.year}&month=${period.month}`),
  });

  if (isLoading || !summary) return <Spinner />;

  const { budget } = summary;

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-3 pt-1 lg:grid-cols-[2fr_3fr] lg:items-end lg:gap-x-10 lg:gap-y-2">
        {budget.configured ? (
          <>
            <div className="flex flex-col-reverse items-center text-center lg:col-start-1 lg:row-start-1 lg:flex-col lg:items-start lg:text-left">
              <p className="tnum mt-1 text-xs text-muted lg:mt-0 lg:mb-1 lg:text-[0.8125rem] lg:first-letter:uppercase">
                restano su {formatEuro(budget.total)}
              </p>
              <p className="tnum text-[1.75rem] leading-none font-semibold tracking-tight lg:text-[2.5rem]">
                {formatEuro(budget.remaining)}
              </p>
            </div>
            <div className="lg:col-span-2 lg:row-start-2">
              <Gauge
                ratio={budget.ratio}
                pace={budget.total > 0 ? budget.expectedToDate / budget.total : 0}
              />
            </div>
            <div className="lg:col-span-2 lg:row-start-3">
              <PaceIndicator
                delta={budget.paceDelta}
                over={budget.ratio > 1}
                day={budget.dayOfMonth}
                days={budget.daysInMonth}
              />
            </div>
            <div className="grid grid-cols-3 lg:col-start-2 lg:row-start-1 lg:gap-6">
              <Tile
                strip
                label="Spese"
                value={formatEuro(summary.expenses, true)}
                valueLg={formatEuro(summary.expenses)}
              />
              <Tile
                strip
                label="Guadagno"
                value={formatEuro(summary.income, true)}
                valueLg={formatEuro(summary.income)}
              />
              <Tile
                strip
                label="Risparmio"
                value={formatEuro(summary.savings, true)}
                valueLg={formatEuro(summary.savings)}
                tone={summary.savings >= 0 ? 'positive' : 'negative'}
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col-reverse items-center text-center lg:col-start-1 lg:row-start-1 lg:flex-col lg:items-start lg:text-left">
              <p className="mt-1 text-xs text-muted lg:mt-0 lg:mb-1 lg:text-[0.8125rem]">
                spesi questo mese
              </p>
              <p className="tnum text-[1.75rem] leading-none font-semibold tracking-tight lg:text-[2.5rem]">
                {formatEuro(summary.expenses)}
              </p>
            </div>
            <div className="grid grid-cols-3 lg:col-start-2 lg:row-start-1 lg:gap-6">
              <Tile
                strip
                label="Spese"
                value={formatEuro(summary.expenses, true)}
                valueLg={formatEuro(summary.expenses)}
              />
              <Tile
                strip
                label="Guadagno"
                value={formatEuro(summary.income, true)}
                valueLg={formatEuro(summary.income)}
              />
              <Tile
                strip
                label="Risparmio"
                value={formatEuro(summary.savings, true)}
                valueLg={formatEuro(summary.savings)}
                tone={summary.savings >= 0 ? 'positive' : 'negative'}
              />
            </div>
            <Link
              to="/budget"
              className="mx-auto rounded-[var(--radius-control)] bg-accent px-4 py-3 text-[1.0625rem] font-semibold text-accent-text lg:col-span-2 lg:mx-0 lg:w-fit"
            >
              Imposta un budget
            </Link>
          </>
        )}
      </section>

      {due && due.length > 0 && (
        <section>
          <SectionTitle>Spese fisse</SectionTitle>
          <div className="flex flex-col gap-2">
            {due.map((r) => (
              <RecurringPrompt key={r.id} rule={r} period={period} />
            ))}
          </div>
        </section>
      )}

      <section>
        {recent && recent.items.length > 0 ? (
          <TransactionTimeline transactions={recent.items} />
        ) : (
          <Card>
            <EmptyState
              title="Ancora nessun movimento"
              hint="Tocca + per registrare la prima spesa."
            />
          </Card>
        )}
      </section>
    </div>
  );
}

/**
 * Confronto fra speso e ritmo teorico: quanto si sarebbe speso a oggi
 * distribuendo il budget in modo uniforme sui giorni del mese.
 */
function PaceIndicator({
  delta,
  over,
  day,
  days,
}: {
  delta: number;
  over: boolean;
  day: number;
  days: number;
}) {
  const ahead = delta >= 0;
  const tone = ahead ? 'text-success' : over ? 'text-danger' : 'text-warning';
  return (
    <p className={`text-center text-[0.8125rem] font-semibold lg:text-left lg:text-sm ${tone}`}>
      {ahead ? 'Sotto' : 'Sopra'} il ritmo di{' '}
      <span className="lg:hidden">{formatEuro(Math.abs(delta), true)}</span>
      <span className="hidden lg:inline">{formatEuro(Math.abs(delta))}</span>
      <span className="hidden lg:inline">
        {' '}
        · giorno {day} di {days}
      </span>
    </p>
  );
}

/** Proposta di spesa fissa: si scrive solo se l'utente conferma. */
function RecurringPrompt({
  rule,
  period,
}: {
  rule: RecurringExpense;
  period: { year: number; month: number };
}) {
  const queryClient = useQueryClient();

  const confirm = useMutation({
    mutationFn: () => api.post(`/recurring/${rule.id}/confirm`, period),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const skip = useMutation({
    mutationFn: () => api.post(`/recurring/${rule.id}/skip`, period),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const busy = confirm.isPending || skip.isPending;

  return (
    <Card className="flex items-center gap-3 py-3">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">
          {rule.category.name} {formatEuro(rule.amount)}
        </span>
        <span className="text-xs text-muted">Registrata?</span>
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => skip.mutate()}
        className="rounded-[var(--radius-control)] bg-surface-2 px-3 py-2 text-sm text-muted disabled:opacity-50"
      >
        Salta
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => confirm.mutate()}
        className="rounded-[var(--radius-control)] bg-accent px-4 py-2 text-sm font-semibold text-accent-text disabled:opacity-50"
      >
        Sì
      </button>
    </Card>
  );
}
